import React from 'react';

import moment from 'moment';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { ExpenseTravels } from 'models/expense';
import { getFormattedPrice } from 'utils/currency';
import { htmlT, formatISODateToDDMMYYYY } from 'utils/generic';

import {
  Item,
  ETicket,
  InfoContainer,
  Title,
  Icon,
  SubTitle,
  ReportLabel,
  Separator,
  Expenses,
  Container,
  CarIcon,
  MoneyIcon,
} from './styles';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';

interface Props {
  travel: ExpenseTravels;
  onClick: (travel: ExpenseTravels) => void;
  testId?: string;
}

interface ExpensesProps {
  travel: ExpenseTravels;
}

const ExpensesTitle = ({ travel }: { travel: ExpenseTravels }) => {
  const { t } = useStoreTranslation();

  if (travel.type === TravelType.Vehicle)
    return (
      <>
        <CarIcon />
        {t('expenseTravelCard.autoTitle', {
          city: travel.city,
        })}
      </>
    );

  if (travel.type === TravelType.Hotel)
    return (
      <>
        <Icon src="/corporate/images/bedroom-outline-16-copy-2.svg" alt="hotel icon" />
        {t('expenseTravelCard.hotelTitle', {
          city: travel.city,
        })}
      </>
    );

  if (travel.type === TravelType.Flight)
    return (
      <>
        <Icon src="/corporate/images/plane-outline-32-copy-3.svg" alt="plane icon" />
        {t('expenseTravelCard.flightTitle', {
          city: travel.city,
        })}
      </>
    );

  if (travel.type === TravelType.ExtraCharge)
    return (
      <>
        <MoneyIcon /> {t('expenseTravelCard.extraCharge')}
      </>
    );

  return null;
};

const ExpensesArea = ({ travel }: ExpensesProps) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number) => {
    const { formatted } = getFormattedPrice({ value, currency: travel.currencyCode, t });
    return formatted;
  };

  return (
    <>
      <Separator />
      <Expenses>
        <Expenses.Value>{formattedPrice(travel.value || 0)}</Expenses.Value>
        <Expenses.Count>
          {travel.quantityExpenses
            ? t('expenseTravelCard.expense', { count: travel.quantityExpenses })
            : t('expenseTravelCard.noExpense')}
        </Expenses.Count>
      </Expenses>
    </>
  );
};

const ExpensesCard = ({ travel, onClick, testId }: Props) => {
  const { t } = useStoreTranslation();
  const formatDate = (date: Date) => moment(date).format(t('expenseTravelCard.dateFormat'));
  const companyConfiguration = useCompanyConfiguration();

  return (
    <>
      <Item onClick={() => onClick(travel)} testId={testId}>
        <Container>
          <ETicket>
            {t('expenseTravelCard.confirmation', {
              companyName: companyConfiguration?.companyName,
              value: travel.confirmation,
            })}
          </ETicket>
          <InfoContainer>
            <Title>
              <ExpensesTitle travel={travel} />
            </Title>
            <SubTitle>
              {formatDate(travel.date)}
              {travel.isGeneratedReport ? (
                <ReportLabel>
                  {htmlT(t, 'expenseTravelCard.lastReport', {
                    date: formatISODateToDDMMYYYY(travel.lastDateGeneratedReport),
                  })}
                </ReportLabel>
              ) : (
                <ReportLabel>{t('expenseTravelCard.noReport')}</ReportLabel>
              )}
            </SubTitle>
            <ExpensesArea travel={travel} />
          </InfoContainer>
        </Container>
      </Item>
    </>
  );
};
export default ExpensesCard;
